import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, Fish, ChevronRight, ChevronDown, X, Loader2, 
  Plus, Clock, MapPin, Camera, GitBranch, Layers, Globe
} from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import SpeciesDetailModal from "@/components/marine/SpeciesDetailModal";
import SpeciesSubmissionModal from "@/components/marine/SpeciesSubmissionModal";
import SubmissionReviewPanel from "@/components/marine/SubmissionReviewPanel";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Category icons mapping
const CATEGORY_ICONS = {
  sharks: "🦈",
  rays: "🐟",
  bony_fish: "🐠",
  eels: "🪱",
  invertebrates: "🐙",
  cnidarians: "🪸",
  turtles: "🐢",
  mammals: "🐬",
  reptiles: "🦎"
};

// Conservation status colors
const CONSERVATION_COLORS = {
  LC: "bg-green-100 text-green-700 border-green-300",
  NT: "bg-yellow-100 text-yellow-700 border-yellow-300",
  VU: "bg-orange-100 text-orange-700 border-orange-300",
  EN: "bg-red-100 text-red-700 border-red-300",
  CR: "bg-red-200 text-red-900 border-red-400",
  DD: "bg-gray-100 text-gray-700 border-gray-300",
  NE: "bg-gray-100 text-gray-600 border-gray-300"
};

const MarineDatabase = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [species, setSpecies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  
  // Explore mode (just controls which panel is visible, not filtering)
  const [exploreMode, setExploreMode] = useState("categories");
  
  // Full taxonomy tree
  const [taxonomyData, setTaxonomyData] = useState(null);
  const [expandedTaxonomy, setExpandedTaxonomy] = useState({});
  
  // Location data
  const [locationData, setLocationData] = useState(null);
  const [expandedLocations, setExpandedLocations] = useState({});
  
  // FILTERS - Can be combined!
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [selectedTaxonomy, setSelectedTaxonomy] = useState(null); // {level, value}
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  
  // Modals
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  
  // Review panel
  const [canReview, setCanReview] = useState(false);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Count active filters
  const activeFilterCount = [
    selectedCategory,
    selectedTaxonomy,
    selectedRegion,
    selectedCountry,
    searchQuery
  ].filter(Boolean).length;

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "null");
    
    if (!currentUser) {
      toast.error("Please log in to access the Marine Database");
      navigate("/account");
      return;
    }
    
    if (currentUser.role === "dive_center") {
      toast.error("Marine Database is not available for dive center accounts");
      navigate("/community");
      return;
    }
    
    setUser(currentUser);
    fetchCategories();
    fetchTaxonomyTree();
    fetchLocationData();
    checkReviewPermission(currentUser);
  }, [navigate]);

  // Fetch species whenever ANY filter changes
  useEffect(() => {
    if (user) {
      fetchSpecies();
    }
  }, [user, selectedCategory, searchQuery, selectedTaxonomy, selectedRegion, selectedCountry]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchTaxonomyTree = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/taxonomy-tree`);
      setTaxonomyData(response.data);
    } catch (error) {
      console.error("Error fetching taxonomy tree:", error);
    }
  };

  const fetchLocationData = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/locations`);
      setLocationData(response.data);
    } catch (error) {
      console.error("Error fetching location data:", error);
    }
  };

  const checkReviewPermission = async (currentUser) => {
    if (currentUser.role === "admin") {
      setCanReview(true);
      fetchPendingCount();
      return;
    }
    
    try {
      const response = await axios.get(`${API}/users/${currentUser.id}/badges`);
      const hasBiologistBadge = response.data.some(
        badge => badge.name?.toLowerCase().includes("marine biologist")
      );
      setCanReview(hasBiologistBadge);
      if (hasBiologistBadge) {
        fetchPendingCount();
      }
    } catch (error) {
      console.error("Error checking badges:", error);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const response = await axios.get(`${API}/marine-database/submissions/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCount(response.data.length);
    } catch (error) {
      console.error("Error fetching pending count:", error);
    }
  };

  const fetchSpecies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Add all active filters
      if (selectedCategory) params.append("category", selectedCategory);
      if (searchQuery) params.append("search", searchQuery);
      if (selectedTaxonomy) {
        params.append("taxonomy_filter", `${selectedTaxonomy.level}:${selectedTaxonomy.value}`);
      }
      if (selectedRegion) params.append("region", selectedRegion);
      if (selectedCountry) params.append("country", selectedCountry);
      params.append("limit", "100");
      
      const response = await axios.get(`${API}/marine-database?${params.toString()}`);
      setSpecies(response.data.species);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching species:", error);
      toast.error("Failed to load marine species");
    } finally {
      setLoading(false);
    }
  };

  // Filter handlers - DON'T clear other filters!
  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(selectedCategory === categoryId ? "" : categoryId);
  };

  const handleTaxonomySelect = (level, value) => {
    if (selectedTaxonomy?.level === level && selectedTaxonomy?.value === value) {
      setSelectedTaxonomy(null);
    } else {
      setSelectedTaxonomy({ level, value });
    }
  };

  const handleRegionSelect = (region) => {
    if (selectedRegion === region) {
      setSelectedRegion(null);
      setSelectedCountry(null); // Clear country when region is cleared
    } else {
      setSelectedRegion(region);
      setSelectedCountry(null); // Reset country when changing region
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(selectedCountry === country ? null : country);
  };

  const toggleTaxonomyExpand = (path) => {
    setExpandedTaxonomy(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const toggleLocationExpand = (path) => {
    setExpandedLocations(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setSelectedTaxonomy(null);
    setSelectedRegion(null);
    setSelectedCountry(null);
    setSearchParams({});
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith("http")) return imageUrl;
    return `${BACKEND_URL}${imageUrl}`;
  };

  // Check if taxonomy level is selected
  const isTaxonomySelected = (level, value) => {
    return selectedTaxonomy?.level === level && selectedTaxonomy?.value === value;
  };

  // Render taxonomy tree - clickable at ANY level
  const renderTaxonomyTree = () => {
    if (!taxonomyData?.tree) return <Loader2 className="w-5 h-5 animate-spin mx-auto" />;
    
    const { tree, species_counts } = taxonomyData;

    return (
      <div className="space-y-1 text-sm max-h-[400px] overflow-y-auto pr-2">
        {Object.entries(tree).map(([phylum, phylumData]) => {
          const isPhylumSelected = isTaxonomySelected("phylum", phylum);
          return (
            <div key={phylum} className="border-l-2 border-cyan-200 pl-2">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => toggleTaxonomyExpand(phylum)}
                  className="p-1 hover:bg-cyan-50 rounded"
                >
                  {expandedTaxonomy[phylum] ? <ChevronDown className="w-4 h-4 text-cyan-600" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </button>
                <button
                  onClick={() => handleTaxonomySelect("phylum", phylum)}
                  className={`flex-1 text-left py-1.5 px-2 rounded transition-colors ${
                    isPhylumSelected ? "bg-cyan-100 ring-2 ring-cyan-400" : "hover:bg-cyan-50"
                  }`}
                >
                  <span className={`font-semibold ${isPhylumSelected ? "text-cyan-900" : "text-cyan-800"}`}>{phylum}</span>
                </button>
              </div>
              
              {expandedTaxonomy[phylum] && phylumData.classes && (
                <div className="ml-3 space-y-0.5">
                  {Object.entries(phylumData.classes).map(([className, classData]) => {
                    const isClassSelected = isTaxonomySelected("class_name", className);
                    return (
                      <div key={className} className="border-l border-blue-200 pl-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleTaxonomyExpand(`${phylum}-${className}`)}
                            className="p-0.5 hover:bg-blue-50 rounded"
                          >
                            {expandedTaxonomy[`${phylum}-${className}`] ? <ChevronDown className="w-3 h-3 text-blue-600" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
                          </button>
                          <button
                            onClick={() => handleTaxonomySelect("class_name", className)}
                            className={`flex-1 text-left py-1 px-2 rounded transition-colors ${
                              isClassSelected ? "bg-blue-100 ring-2 ring-blue-400" : "hover:bg-blue-50"
                            }`}
                          >
                            <span className={`font-medium ${isClassSelected ? "text-blue-900" : "text-blue-700"}`}>{className}</span>
                          </button>
                        </div>
                        
                        {expandedTaxonomy[`${phylum}-${className}`] && classData.orders && (
                          <div className="ml-3 space-y-0.5">
                            {Object.entries(classData.orders).map(([order, orderData]) => {
                              const isOrderSelected = isTaxonomySelected("order", order);
                              return (
                                <div key={order} className="border-l border-purple-200 pl-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => toggleTaxonomyExpand(`${phylum}-${className}-${order}`)}
                                      className="p-0.5 hover:bg-purple-50 rounded"
                                    >
                                      {expandedTaxonomy[`${phylum}-${className}-${order}`] ? <ChevronDown className="w-3 h-3 text-purple-600" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
                                    </button>
                                    <button
                                      onClick={() => handleTaxonomySelect("order", order)}
                                      className={`flex-1 text-left py-0.5 px-2 rounded transition-colors ${
                                        isOrderSelected ? "bg-purple-100 ring-2 ring-purple-400" : "hover:bg-purple-50"
                                      }`}
                                    >
                                      <span className={isOrderSelected ? "text-purple-900 font-medium" : "text-purple-700"}>{order}</span>
                                    </button>
                                  </div>
                                  
                                  {expandedTaxonomy[`${phylum}-${className}-${order}`] && orderData.families && (
                                    <div className="ml-3 space-y-0.5 py-1">
                                      {Object.entries(orderData.families).map(([family, familyData]) => {
                                        const count = species_counts?.[family] || 0;
                                        const isFamilySelected = isTaxonomySelected("family", family);
                                        return (
                                          <button
                                            key={family}
                                            onClick={() => handleTaxonomySelect("family", family)}
                                            className={`flex items-center gap-2 w-full text-left py-1 px-2 rounded transition-colors text-xs ${
                                              isFamilySelected
                                                ? "bg-pink-100 text-pink-800 font-medium ring-2 ring-pink-400"
                                                : "hover:bg-gray-100 text-gray-600"
                                            }`}
                                          >
                                            <span className="italic">{family}</span>
                                            {familyData.common_name && (
                                              <span className="text-gray-400 truncate">({familyData.common_name})</span>
                                            )}
                                            {count > 0 && (
                                              <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1">
                                                {count}
                                              </Badge>
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render location tree
  const renderLocationTree = () => {
    if (!locationData?.all_regions) return <Loader2 className="w-5 h-5 animate-spin mx-auto" />;
    
    const { all_regions, regions_with_sightings } = locationData;
    
    const sightingsMap = {};
    regions_with_sightings?.forEach(r => {
      sightingsMap[r.region] = r;
    });

    return (
      <div className="space-y-1 text-sm max-h-[400px] overflow-y-auto pr-2">
        {Object.entries(all_regions).map(([region, regionData]) => {
          const hasSightings = sightingsMap[region];
          const countriesWithSightings = hasSightings?.countries || [];
          const isRegionSelected = selectedRegion === region;
          
          return (
            <div key={region} className="border-l-2 border-emerald-200 pl-2">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => toggleLocationExpand(region)}
                  className="p-1 hover:bg-emerald-50 rounded"
                >
                  {expandedLocations[region] ? <ChevronDown className="w-4 h-4 text-emerald-600" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </button>
                <button
                  onClick={() => handleRegionSelect(region)}
                  className={`flex-1 flex items-center gap-1 text-left py-1.5 px-2 rounded transition-colors ${
                    isRegionSelected 
                      ? "bg-emerald-100 ring-2 ring-emerald-400" 
                      : "hover:bg-emerald-50"
                  } ${!hasSightings ? "opacity-50" : ""}`}
                >
                  <Globe className="w-3 h-3 text-emerald-600" />
                  <span className={`font-semibold ${hasSightings ? "text-emerald-800" : "text-gray-500"}`}>
                    {region}
                  </span>
                  {hasSightings && (
                    <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1 bg-emerald-100">
                      {hasSightings.total_species}
                    </Badge>
                  )}
                </button>
              </div>
              
              {expandedLocations[region] && (
                <div className="ml-5 space-y-0.5 py-1">
                  {countriesWithSightings.length > 0 && (
                    <>
                      {countriesWithSightings.map(({ country, species_count }) => {
                        const isCountrySelected = selectedCountry === country;
                        return (
                          <button
                            key={country}
                            onClick={() => handleCountrySelect(country)}
                            className={`flex items-center gap-2 w-full text-left py-1 px-2 rounded transition-colors text-xs ${
                              isCountrySelected
                                ? "bg-emerald-100 text-emerald-800 font-medium ring-2 ring-emerald-400"
                                : "hover:bg-gray-100 text-gray-700"
                            }`}
                          >
                            <MapPin className="w-3 h-3 text-emerald-500" />
                            <span>{country}</span>
                            <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1">
                              {species_count}
                            </Badge>
                          </button>
                        );
                      })}
                    </>
                  )}
                  
                  {regionData.countries.filter(c => !countriesWithSightings.find(cs => cs.country === c)).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => toggleLocationExpand(`${region}-other`)}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                      >
                        {expandedLocations[`${region}-other`] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        <span>Other countries (no sightings yet)</span>
                      </button>
                      
                      {expandedLocations[`${region}-other`] && (
                        <div className="ml-4 mt-1 space-y-0.5">
                          {regionData.countries
                            .filter(c => !countriesWithSightings.find(cs => cs.country === c))
                            .map(country => (
                              <div key={country} className="text-xs text-gray-400 py-0.5 px-2">
                                {country}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {countriesWithSightings.length === 0 && (
                    <div className="text-xs text-gray-400 italic py-2 px-2">
                      No species sightings recorded yet
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <SEO 
        title="Marine Life Database - George Dives" 
        description="Explore our comprehensive marine life database featuring species from the Indian Ocean and beyond" 
      />
      <Navigation />
      <FloatingUserMenu />
      
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Fish className="w-10 h-10 text-cyan-600" />
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Marine Life Database</h1>
            </div>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Explore and contribute to our growing database of marine species. 
              Combine filters from categories, taxonomy, and locations.
            </p>
            
            {/* Action Buttons */}
            <div className="flex justify-center gap-3 mt-6 flex-wrap">
              <Button
                onClick={() => setShowSubmitModal(true)}
                className="bg-cyan-500 hover:bg-cyan-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Submit Species
              </Button>
              
              {canReview && (
                <Button
                  variant="outline"
                  onClick={() => setShowReviewPanel(true)}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50 relative"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Review Submissions
                  {pendingCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {pendingCount}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by species name, dive site, or location..."
                className="pl-10 h-12"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Active Filters Bar */}
          {activeFilterCount > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-3 mb-6 max-w-4xl mx-auto">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-500">Active filters:</span>
                
                {searchQuery && (
                  <Badge variant="secondary" className="gap-1 px-3 py-1">
                    <Search className="w-3 h-3" />
                    &quot;{searchQuery}&quot;
                    <X className="w-3 h-3 cursor-pointer ml-1 hover:text-red-500" onClick={() => handleSearch("")} />
                  </Badge>
                )}
                
                {selectedCategory && (
                  <Badge className="gap-1 px-3 py-1 bg-cyan-100 text-cyan-800 hover:bg-cyan-200">
                    {CATEGORY_ICONS[selectedCategory]} {categories.find(c => c.id === selectedCategory)?.name}
                    <X className="w-3 h-3 cursor-pointer ml-1 hover:text-red-500" onClick={() => setSelectedCategory("")} />
                  </Badge>
                )}
                
                {selectedTaxonomy && (
                  <Badge className="gap-1 px-3 py-1 bg-purple-100 text-purple-800 hover:bg-purple-200">
                    <GitBranch className="w-3 h-3" />
                    {selectedTaxonomy.value}
                    <X className="w-3 h-3 cursor-pointer ml-1 hover:text-red-500" onClick={() => setSelectedTaxonomy(null)} />
                  </Badge>
                )}
                
                {selectedRegion && (
                  <Badge className="gap-1 px-3 py-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
                    <Globe className="w-3 h-3" />
                    {selectedRegion}
                    <X className="w-3 h-3 cursor-pointer ml-1 hover:text-red-500" onClick={() => { setSelectedRegion(null); setSelectedCountry(null); }} />
                  </Badge>
                )}
                
                {selectedCountry && (
                  <Badge className="gap-1 px-3 py-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
                    <MapPin className="w-3 h-3" />
                    {selectedCountry}
                    <X className="w-3 h-3 cursor-pointer ml-1 hover:text-red-500" onClick={() => setSelectedCountry(null)} />
                  </Badge>
                )}
                
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-gray-500 h-7 ml-auto">
                  Clear all
                </Button>
              </div>
            </div>
          )}

          {/* Explore Mode Tabs */}
          <div className="flex justify-center gap-2 mb-6">
            <Button
              variant={exploreMode === "categories" ? "default" : "outline"}
              size="sm"
              onClick={() => setExploreMode("categories")}
              className={`${exploreMode === "categories" ? "bg-cyan-500 hover:bg-cyan-600" : ""} ${selectedCategory ? "ring-2 ring-cyan-400 ring-offset-1" : ""}`}
            >
              <Layers className="w-4 h-4 mr-2" />
              Categories
              {selectedCategory && <span className="ml-1 w-2 h-2 bg-cyan-300 rounded-full" />}
            </Button>
            <Button
              variant={exploreMode === "taxonomy" ? "default" : "outline"}
              size="sm"
              onClick={() => setExploreMode("taxonomy")}
              className={`${exploreMode === "taxonomy" ? "bg-purple-500 hover:bg-purple-600" : ""} ${selectedTaxonomy ? "ring-2 ring-purple-400 ring-offset-1" : ""}`}
            >
              <GitBranch className="w-4 h-4 mr-2" />
              Taxonomy
              {selectedTaxonomy && <span className="ml-1 w-2 h-2 bg-purple-300 rounded-full" />}
            </Button>
            <Button
              variant={exploreMode === "locations" ? "default" : "outline"}
              size="sm"
              onClick={() => setExploreMode("locations")}
              className={`${exploreMode === "locations" ? "bg-emerald-500 hover:bg-emerald-600" : ""} ${(selectedRegion || selectedCountry) ? "ring-2 ring-emerald-400 ring-offset-1" : ""}`}
            >
              <Globe className="w-4 h-4 mr-2" />
              Locations
              {(selectedRegion || selectedCountry) && <span className="ml-1 w-2 h-2 bg-emerald-300 rounded-full" />}
            </Button>
          </div>

          {/* Categories Grid */}
          {exploreMode === "categories" && (
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3 mb-8 max-w-6xl mx-auto">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  className={`p-3 rounded-xl text-center transition-all ${
                    selectedCategory === category.id
                      ? "bg-cyan-100 border-2 border-cyan-500 shadow-md ring-2 ring-cyan-400"
                      : "bg-white border border-gray-200 hover:border-cyan-300 hover:shadow"
                  }`}
                >
                  <div className="text-2xl mb-1">{category.icon}</div>
                  <div className="text-xs font-medium text-gray-700 leading-tight">{category.name}</div>
                </button>
              ))}
            </div>
          )}

          {/* Taxonomy Tree */}
          {exploreMode === "taxonomy" && (
            <Card className="max-w-2xl mx-auto mb-8 border-purple-200">
              <CardContent className="pt-4">
                <div className="mb-3 pb-2 border-b">
                  <h3 className="font-medium text-gray-700">Taxonomic Classification</h3>
                  <p className="text-xs text-gray-500">Click on a family to filter • Combines with other filters</p>
                </div>
                {renderTaxonomyTree()}
              </CardContent>
            </Card>
          )}

          {/* Location Tree */}
          {exploreMode === "locations" && (
            <Card className="max-w-2xl mx-auto mb-8 border-emerald-200">
              <CardContent className="pt-4">
                <div className="mb-3 pb-2 border-b">
                  <h3 className="font-medium text-gray-700">Filter by Location</h3>
                  <p className="text-xs text-gray-500">Click region or country to filter • Combines with other filters</p>
                </div>
                {renderLocationTree()}
                {locationData?.regions_with_sightings?.length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No location data yet</p>
                    <p className="text-sm">Submit species sightings to populate this filter!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Results Count */}
          <div className="text-center mb-6">
            <p className="text-gray-600">
              {loading ? (
                "Loading..."
              ) : (
                <>
                  <span className="font-semibold text-gray-800">{total}</span> species found
                  {activeFilterCount > 0 && (
                    <span className="text-gray-400"> (filtered by {activeFilterCount} criteria)</span>
                  )}
                </>
              )}
            </p>
          </div>

          {/* Species Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
          ) : species.length === 0 ? (
            <div className="text-center py-12">
              <Fish className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No species found</h3>
              <p className="text-gray-500 mb-4">No species match your current filter combination</p>
              {activeFilterCount > 1 && (
                <p className="text-sm text-cyan-600">
                  Try removing some filters to broaden your search
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-7xl mx-auto">
              {species.map((item) => (
                <Card
                  key={item.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow overflow-hidden group"
                  onClick={() => setSelectedSpecies(item)}
                >
                  {/* Image */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-cyan-100 to-blue-100 relative overflow-hidden">
                    {item.images && item.images.length > 0 ? (
                      <img
                        src={getImageUrl(item.images[0])}
                        alt={item.common_names[0]}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`absolute inset-0 flex items-center justify-center ${item.images?.length ? 'hidden' : ''}`}
                      style={{ display: item.images?.length ? 'none' : 'flex' }}
                    >
                      <span className="text-6xl opacity-50">{CATEGORY_ICONS[item.simple_category] || "🐟"}</span>
                    </div>
                    
                    {/* Badges container — constrained within card boundaries */}
                    <div className="absolute top-1.5 left-1.5 right-1.5 flex justify-between items-start gap-1 pointer-events-none">
                      <Badge className="bg-white/90 text-gray-700 border-0 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 shrink min-w-0 truncate pointer-events-auto">
                        {CATEGORY_ICONS[item.simple_category]} <span className="hidden sm:inline">{categories.find(c => c.id === item.simple_category)?.name || item.simple_category}</span>
                      </Badge>
                      {item.conservation_status && ["EN", "CR", "VU"].includes(item.conservation_status) && (
                        <Badge className={`border text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 shrink-0 pointer-events-auto ${CONSERVATION_COLORS[item.conservation_status]}`}>
                          {item.conservation_status}
                        </Badge>
                      )}
                    </div>

                    {item.images && item.images.length > 1 && (
                      <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Camera className="w-3 h-3" />
                        {item.images.length}
                      </div>
                    )}
                  </div>

                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">
                      {item.common_names[0]}
                    </h3>
                    <p className="text-sm text-gray-500 italic mb-2 line-clamp-1">
                      {item.scientific_name}
                    </p>
                    
                    {item.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                        {item.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {item.locations && item.locations.length > 0 && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {item.locations.length}
                        </span>
                      )}
                      {item.sighting_count > 0 && (
                        <span className="flex items-center gap-1">
                          👁 {item.sighting_count}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedSpecies && (
        <SpeciesDetailModal
          species={selectedSpecies}
          onClose={() => setSelectedSpecies(null)}
          canEdit={canReview}
          onUpdate={() => {
            fetchSpecies();
            fetchLocationData();
            setSelectedSpecies(null);
          }}
          onDelete={() => {
            fetchSpecies();
            fetchLocationData();
          }}
        />
      )}

      {showSubmitModal && (
        <SpeciesSubmissionModal
          onClose={() => setShowSubmitModal(false)}
          onSubmit={() => {
            setShowSubmitModal(false);
            toast.success("Species submitted for review!");
            if (canReview) fetchPendingCount();
          }}
        />
      )}

      {showReviewPanel && canReview && (
        <SubmissionReviewPanel
          onClose={() => setShowReviewPanel(false)}
          onReviewComplete={() => {
            fetchPendingCount();
            fetchSpecies();
            fetchLocationData();
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default MarineDatabase;
